import React, { useEffect, useMemo, useState } from 'react';
import { TrendingUp, ExternalLink, Download, Printer } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AccountingLayout } from '../components/AccountingLayout';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { reportProfitLoss, downloadExport } from '../lib/api';
import { formatRp, MONTHS_ID, daysInMonth } from '../lib/format';

const todayIso = () => new Date().toISOString().slice(0, 10);
const pad = (n) => String(n).padStart(2, '0');

const Section = ({ title, rows, total, totalLabel, positive = true, onRowClick }) => (
  <div className="card-modern overflow-hidden mb-4">
    <div className="px-5 py-3 border-b border-stone-200 bg-stone-50">
      <div className="font-heading font-semibold text-stone-900">{title}</div>
    </div>
    <table className="w-full text-sm">
      <tbody>
        {rows.map((r) => {
          const clickable = onRowClick && r.account_id && !String(r.account_id).startsWith('__');
          return (
            <tr
              key={r.account_id}
              className={`border-b border-stone-100 group ${clickable ? 'cursor-pointer hover:bg-emerald-50/50 transition-colors' : ''}`}
              onClick={clickable ? () => onRowClick(r) : undefined}
              data-testid={clickable ? `report-account-row-${r.code}` : undefined}
              title={clickable ? 'Lihat buku besar' : undefined}
            >
              <td className="px-5 py-2 font-mono text-xs text-stone-500 w-24">{r.code}</td>
              <td className="px-2 py-2 text-stone-800">
                <span className="inline-flex items-center gap-1.5">
                  {r.name}
                  {clickable && (
                    <ExternalLink className="w-3 h-3 text-stone-300 group-hover:text-emerald-700 transition-colors" />
                  )}
                </span>
              </td>
              <td className={`px-5 py-2 text-right num font-medium ${r.balance < 0 ? 'text-red-700' : 'text-stone-900'}`}>
                {formatRp(r.balance)}
              </td>
            </tr>
          );
        })}
        {rows.length === 0 && (
          <tr>
            <td colSpan={3} className="px-5 py-6 text-center text-stone-400 text-sm">Belum ada transaksi.</td>
          </tr>
        )}
      </tbody>
      <tfoot className="bg-stone-50 border-t-2 border-stone-300">
        <tr>
          <td colSpan={2} className="px-5 py-3 font-semibold text-stone-900">
            {totalLabel || `Total ${title}`}
          </td>
          <td className={`px-5 py-3 text-right num font-bold ${!positive ? 'text-red-700' : 'text-emerald-900'}`}>
            {formatRp(total)}
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
);

export default function ReportsPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('month'); // 'month' | 'custom'
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [start, setStart] = useState(() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [end, setEnd] = useState(todayIso());
  const [pl, setPl] = useState(null);
  const [loading, setLoading] = useState(false);

  const yearOptions = useMemo(() => {
    const y = now.getFullYear();
    return [y - 2, y - 1, y, y + 1];
    // eslint-disable-next-line
  }, []);

  const monthRange = useMemo(() => {
    const s = `${year}-${pad(month)}-01`;
    const e = `${year}-${pad(month)}-${pad(daysInMonth(year, month))}`;
    return { start: s, end: e };
  }, [month, year]);

  useEffect(() => {
    if (mode === 'month') {
      setStart(monthRange.start);
      setEnd(monthRange.end);
    }
  }, [mode, monthRange]);

  useEffect(() => {
    setLoading(true);
    reportProfitLoss({ start, end })
      .then(setPl)
      .finally(() => setLoading(false));
  }, [start, end]);

  return (
    <AccountingLayout>
        <div className="mb-6 flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold mb-2">
              JurnalKu · Laporan Keuangan
            </div>
            <h1 className="font-heading text-3xl font-semibold text-stone-900 tracking-tight flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-emerald-800" /> Laporan L/R
            </h1>
            <p className="text-sm text-stone-600 mt-2">
              Ringkasan pendapatan dan beban untuk periode terpilih.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => window.print()}
            className="border-stone-300 text-stone-700 hover:bg-stone-100"
            data-testid="print-pl"
          >
            <Printer className="w-4 h-4 mr-1.5" /> Cetak / PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => downloadExport('/exports/profit-loss.xlsx', { start, end }, `laporan-lr-${start}-${end}.xlsx`)}
            className="border-emerald-200 text-emerald-800 hover:bg-emerald-50"
            data-testid="export-pl-xlsx"
          >
            <Download className="w-4 h-4 mr-1.5" /> Ekspor Excel
          </Button>
        </div>

        <div className="card-modern p-4 mb-4 flex flex-wrap gap-3 items-end">
          <div className="min-w-[180px]">
            <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Mode Periode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="bg-white" data-testid="report-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Per Bulan</SelectItem>
                <SelectItem value="custom">Kustom (Tanggal)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {mode === 'month' ? (
            <>
              <div className="min-w-[160px]">
                <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Bulan</Label>
                <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                  <SelectTrigger className="bg-white" data-testid="report-month">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS_ID.map((m, i) => (
                      <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[120px]">
                <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Tahun</Label>
                <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                  <SelectTrigger className="bg-white" data-testid="report-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="text-[11px] text-stone-500 pb-2.5 ml-2" data-testid="report-range-label">
                Periode: <span className="font-mono text-stone-700">{start}</span> — <span className="font-mono text-stone-700">{end}</span>
              </div>
            </>
          ) : (
            <>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Periode Dari</Label>
                <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-white" data-testid="report-start" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">Sampai</Label>
                <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-white" data-testid="report-end" />
              </div>
            </>
          )}
        </div>

        {pl && (
          <div data-testid="pl-report">
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="card-modern p-5">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">Total Pendapatan</div>
                <div className="num text-2xl font-bold text-emerald-800 mt-1">{formatRp(pl.total_revenue)}</div>
              </div>
              <div className="card-modern p-5">
                <div className="text-[10px] uppercase tracking-widest text-stone-500">Total Beban</div>
                <div className="num text-2xl font-bold text-red-700 mt-1">{formatRp(pl.total_expense)}</div>
              </div>
              <div className={`card-modern p-5 border-2 ${pl.net_income >= 0 ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`}>
                <div className="text-[10px] uppercase tracking-widest text-stone-600">Laba / (Rugi) Bersih</div>
                <div className={`num text-2xl font-bold mt-1 ${pl.net_income >= 0 ? 'text-emerald-900' : 'text-red-800'}`}>
                  {formatRp(pl.net_income)}
                </div>
              </div>
            </div>
            <Section title="Pendapatan" rows={pl.revenue} total={pl.total_revenue}
              onRowClick={(r) => navigate(`/ledger?account_id=${r.account_id}&start=${start}&end=${end}`)} />
            <Section title="Beban" rows={pl.expense} total={pl.total_expense} positive={false}
              onRowClick={(r) => navigate(`/ledger?account_id=${r.account_id}&start=${start}&end=${end}`)} />
          </div>
        )}
    </AccountingLayout>
  );
}
